import React from 'react';
import { Delete, Shuffle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface StickyActionBarProps {
  onQuickPick: () => void;
  onFavouritePick: () => void;
  onClear: () => void;
  hasAnyPicks?: boolean;
  className?: string;
  /** When absent/false the Popular Pick button is hidden. Driven by style_config.showPopularPicks. */
  showFavouritePick?: boolean;
}

/**
 * ActionBar - Quick Pick, Fav Picks, and Clear buttons above the questions list.
 */
const StickyActionBar: React.FC<StickyActionBarProps> = ({
  onQuickPick,
  onFavouritePick,
  onClear,
  hasAnyPicks = false,
  className = '',
  showFavouritePick = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`z-30 bg-app-background px-3 sm:px-4 py-2 w-full overflow-hidden mb-1 ${className}`}
      data-test-id="sticky-action-bar"
    >
      <div className="flex items-center justify-between gap-2 w-full min-w-0">
        {/* Left-aligned buttons: Quick Pick and Fav Picks */}
        <div className="flex gap-2 min-w-0 flex-1">
          <Button
            variant="outline"
            onClick={onQuickPick}
            className="rounded-full border border-border bg-transparent text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
            data-test-id="quick-pick-button"
          >
            <span className="text-xs sm:text-sm">{t('actions.quickPick')}</span>
            <Shuffle className="w-4 h-4 ml-1.5 sm:ml-2 flex-shrink-0" />
          </Button>

          {showFavouritePick && (
            <Button
              variant="outline"
              onClick={onFavouritePick}
              className="rounded-full border border-border bg-transparent text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
              data-test-id="favourite-pick-button"
            >
              <span className="text-xs sm:text-sm">{t('actions.favouritePick')}</span>
              <Star className="w-4 h-4 ml-1.5 sm:ml-2 flex-shrink-0" fill="currentColor" />
            </Button>
          )}
        </div>

        {/* Right-aligned button: Clear - Only show if picks have been made */}
        {hasAnyPicks && (
          <Button
            variant="outline"
            onClick={onClear}
            className="rounded-lg border-0 bg-transparent text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap flex-shrink-0 px-3 sm:px-4"
            data-test-id="clear-button"
          >
            <span className="text-xs sm:text-sm">{t('actions.clear')}</span>
            <div className="w-4 h-4 ml-1.5 sm:ml-2 flex items-center justify-center bg-app-surface-elevated rounded-sm flex-shrink-0">
              <Delete className="w-3 h-3 text-foreground" />
            </div>
          </Button>
        )}
      </div>
    </div>
  );
};

export default StickyActionBar;
